/**
 * Types and functions for working with 2d vectors (Vec2).
 * @module
 */
import degreeToRadian from "common/utils/degreeToRadian";
import { ANGLE_EPSILON, DIST_EPSILON, ONE_MINUS_DIST_EPSILON, ZERO_EPSILON } from "./core";

const { sqrt, abs, sin, cos } = Math;

export type Vec2 = number[];

// we reuse this variable when returning vec2s. This avoids unnecessary allocation
// in my benchmarks, building a new array is 10x slower in Chrome
// https://jsperf.com/vec2 some engines don't see quite this boost

export const vec2Result = [0, 0];

export function vec2Copy(vec: Vec2): Vec2 {
  const v: number[] = [];
  return Object.assign(v, vec);
}

export function vec2Add(x0: number, y0: number, x1: number, y1: number) {
  vec2Result[0] = x0 + x1;
  vec2Result[1] = y0 + y1;
  return vec2Result;
}

export function vec2Sub(x0: number, y0: number, x1: number, y1: number) {
  vec2Result[0] = x0 - x1;
  vec2Result[1] = y0 - y1;
  return vec2Result;
}

export function vec2Scale(x: number, y: number, s: number) {
  vec2Result[0] = x * s;
  vec2Result[1] = y * s;
  return vec2Result;
}

export function vec2Rotate(x: number, y: number, radians: number) {
  const s = sin(radians);
  const c = cos(radians);

  vec2Result[0] = x * c - y * s;
  vec2Result[1] = x * s + y * c;

  return vec2Result;
}

export function vec2RotateDegrees(x: number, y: number, degrees: number) {
  return vec2Rotate(x, y, degreeToRadian(degrees));
}

export function vec2RotateAround(x: number, y: number, radians: number, centerX: number = 0, centerY: number = 0) {
  const s = sin(radians);
  const c = cos(radians);
  const dx = x - centerX;
  const dy = y - centerY;

  vec2Result[0] = dx * c - dy * s + centerX;
  vec2Result[1] = dx * s + dy * c + centerY;

  return vec2Result;
}

export function vec2SetDist(ax: number, ay: number, bx: number, by: number, constraintLength: number): Vec2 {
  if (constraintLength === 0) {
    return [ax, ay];
  }

  const len = vec2Dist(ax, ay, bx, by);
  if (len > DIST_EPSILON) {
    let [dx, dy] = vec2Sub(bx, by, ax, ay);
    [dx, dy] = vec2Scale(dx, dy, constraintLength / len);
    [bx, by] = vec2Add(ax, ay, dx, dy);
  }

  return [bx, by];
}

// vec2Perp provides the tangent of the line rotated by 90 degrees in the CW direction. IOW, the perpendicular vector
// to the "right" of the vector.
export function vec2Perp(x: number, y: number) {
  vec2Result[0] = y;
  vec2Result[1] = -x;
  return vec2Result;
}

export function vec2ArePerp(x0: number, y0: number, x1: number, y1: number, tolerance: number = DIST_EPSILON) {
  return Math.abs(vec2Dot(x0, y0, x1, y1)) < tolerance;
}

export function vec2Dist(x0: number, y0: number, x1: number, y1: number) {
  const dx = x0 - x1;
  const dy = y0 - y1;
  return sqrt(dx * dx + dy * dy);
}

export function vec2MaxOrthoDist(x0: number, y0: number, x1: number, y1: number) {
  const dx = x0 - x1;
  const dy = y0 - y1;
  return Math.max(Math.abs(dx), Math.abs(dy));
}

export function vec2DistSquared(x0: number, y0: number, x1: number, y1: number) {
  const dx = x0 - x1;
  const dy = y0 - y1;
  return dx * dx + dy * dy;
}

export function vec2Normalize(x: number, y: number) {
  const invlen = 1 / sqrt(x * x + y * y);
  vec2Result[0] = x * invlen;
  vec2Result[1] = y * invlen;
  return vec2Result;
}

export function vec2Lerp(x0: number, y0: number, x1: number, y1: number, t: number): Vec2 {
  const dx = x1 - x0;
  const dy = y1 - y0;

  vec2Result[0] = x0 + dx * t;
  vec2Result[1] = y0 + dy * t;

  return vec2Result;
}

export function vec2Unlerp(x0: number, y0: number, x1: number, y1: number, x2: number, y2: number): number {
  const dir = vec2Dot(x1 - x0, y1 - y0, x2 - x0, y2 - y0) < 0 ? -1 : 1;
  const t = vec2Dist(0, 0, x2 - x0, y2 - y0) / vec2Dist(0, 0, x1 - x0, y1 - y0);
  return dir * t;
}

export function vec2SequenceIsTurning(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  angleThreshold: number = ANGLE_EPSILON,
): boolean {
  const [dx0, dy0] = vec2Sub(x1, y1, x0, y0);
  const [dx1, dy1] = vec2Sub(x2, y2, x1, y1);

  const angle = abs(vec2AngleBetween(dx0, dy0, dx1, dy1));

  return angle > angleThreshold && angle < Math.PI * 2 - angleThreshold;
}

export function vec2SequenceIsFullTurn(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  epsilon: number = ANGLE_EPSILON,
): boolean {
  const [dx0, dy0] = vec2Sub(x1, y1, x0, y0);
  const [dx1, dy1] = vec2Sub(x2, y2, x1, y1);

  const angle = vec2AngleBetween(dx0, dy0, dx1, dy1);

  return abs(abs(angle) - Math.PI) < epsilon;
}

export function vec2Dot(x0: number, y0: number, x1: number, y1: number) {
  return x0 * x1 + y0 * y1;
}

export function vec2Len(x: number, y: number): number {
  return sqrt(x * x + y * y);
}

export function vec2LenSquared(x: number, y: number): number {
  return x * x + y * y;
}

export function vec2Cross(x0: number, y0: number, x1: number, y1: number): number {
  return y1 * x0 - y0 * x1;
}

export function vec2IsBetween(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  testx: number,
  testy: number,
  tol: number = ZERO_EPSILON,
  dotTol: number = 0,
) {
  const dx = x1 - x0;
  const dy = y1 - y0;

  const dx0 = testx - x0;
  const dy0 = testy - y0;

  const crossproduct = vec2Cross(dx, dy, dx0, dy0);

  // compare versus epsilon for floating point values
  if (abs(crossproduct) > tol) {
    return false;
  }

  const dotproduct = dx0 * dx + dy0 * dy;
  if (dotproduct < -dotTol) {
    return false;
  }

  const squaredlengthba = dx * dx + dy * dy;
  if (dotproduct > squaredlengthba + dotTol) {
    return false;
  }

  return true;
}

//V3 - Variant 3
//This function has a similar purpose as vec2IsBetween and uses a
//vector length based approach for asserting if a vector/point is between two other vectors/points.
export function vec2IsBetweenUsingLengthBasedCalc(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  testx: number,
  testy: number,
  tol: number = ZERO_EPSILON,
) {
  const dx = x1 - x0;
  const dy = y1 - y0;
  const dx0 = testx - x0;
  const dy0 = testy - y0;
  const dx1 = x1 - testx;
  const dy1 = y1 - testy;
  const l1 = Math.sqrt(dx0 * dx0 + dy0 * dy0);
  const l2 = Math.sqrt(dx1 * dx1 + dy1 * dy1);
  const l3 = Math.sqrt(dx * dx + dy * dy);
  return Math.abs(l1 + l2 - l3) < tol;
}

export function vec2IsInEdgeBounds(
  x: number,
  y: number,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  inclusive: boolean = false,
) {
  const [vStartX, vStartY] = vec2Sub(x, y, x0, y0); // edge start to pt
  const [vEndX, vEndY] = vec2Sub(x, y, x1, y1); // edge end to pt
  const [vEdgeX, vEdgeY] = vec2Sub(x1, y1, x0, y0); // edge start to edge end;

  const startLen2 = vec2LenSquared(vStartX, vStartY);
  const endLen2 = vec2LenSquared(vEndX, vEndY);
  const edgeLen2 = vec2LenSquared(vEdgeX, vEdgeY);

  if (inclusive) {
    return edgeLen2 + startLen2 >= endLen2 && edgeLen2 + endLen2 >= startLen2;
  }

  return edgeLen2 + startLen2 > endLen2 && edgeLen2 + endLen2 > startLen2;
}

export function vec2ToString(x: number, y: number): string {
  return `[${x}, ${y}]`;
}

export function vec2Negate(x: number, y: number) {
  vec2Result[0] = -x;
  vec2Result[1] = -y;

  return vec2Result;
}

export function vec2AngleBetween(x0: number, y0: number, x1: number, y1: number): number {
  return Math.atan2(y1, x1) - Math.atan2(y0, x0);
}

// the points are expected to be a sequence of 3 points along a chain
export function vec2Bisect(x0: number, y0: number, x1: number, y1: number, x2: number, y2: number): number[] {
  const d1x = x1 - x0;
  const d1y = y1 - y0;

  const d2x = x2 - x1;
  const d2y = y2 - y1;

  const perp1x = d1y;
  const perp1y = -d1x;

  const perp2x = d2y;
  const perp2y = -d2x;

  const d1 = 1 / sqrt(d1x * d1x + d1y * d1y);
  const d2 = 1 / sqrt(d2x * d2x + d2y * d2y);

  const bix = perp1x * d1 + perp2x * d2;
  const biy = perp1y * d1 + perp2y * d2;

  const bl = 1 / sqrt(bix * bix + biy * biy);

  return [bix * bl, biy * bl];
}

export function vec2AngleBetweenCW(x0: number, y0: number, x1: number, y1: number): number {
  const dotProduct = x0 * x1 + y0 * y1;
  const determinant = x0 * y1 - y0 * x1;
  const angle = Math.atan2(determinant, dotProduct);

  if (angle === 0) {
    return 0;
  }
  if (Math.abs(angle) === Math.PI) {
    return Math.PI;
  }
  if (angle < 0) {
    return Math.abs(angle);
  }
  return 2 * Math.PI - angle;
}

export function vec2AngleBetweenCWSimple(x0: number, y0: number, x1: number, y1: number): number {
  const dotProduct = x0 * x1 + y0 * y1;
  const determinant = x0 * y1 - y0 * x1;
  return Math.atan2(determinant, dotProduct);
}

/**
 * Returns true if normals (nx0,ny0) and (nx1, ny1) are the same or mirrored around the origin.
 */
export function vec2NormsCollinear(nx0: number, ny0: number, nx1: number, ny1: number): boolean {
  return Math.abs(vec2Dot(nx0, ny0, nx1, ny1)) > ONE_MINUS_DIST_EPSILON;
}

export function vec2IsNear(x0: number, y0: number, x1: number, y1: number, tolerance: number = 0.01) {
  return Math.pow(x0 - x1, 2) + Math.pow(y0 - y1, 2) <= Math.pow(tolerance, 2);
}

/**
 * Projects vector U onto vector V.
 * https://en.wikipedia.org/wiki/Vector_projection
 */
export function vec2Project(x0: number, y0: number, x1: number, y1: number): Vec2 {
  if ((x0 === 0 && y0 === 0) || (x1 === 0 && y1 === 0)) {
    return [0, 0];
  }
  return vec2Scale(x1, y1, vec2ProjectScaleFactor(x0, y0, x1, y1));
}

export function vec2ProjectScaleFactor(x0: number, y0: number, x1: number, y1: number): number {
  const lenSq = vec2Dot(x1, y1, x1, y1);
  if (lenSq < DIST_EPSILON) {
    return 0;
  }
  return vec2Dot(x1, y1, x0, y0) / lenSq;
}

/**
 * Signed distance from the line (x0,y0),(x1,y1) to the point (px,py)
 */
export function vec2SignedDistance(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  px: number,
  py: number,
): number | undefined {
  const crossproduct = vec2Cross(x1 - x0, y1 - y0, px - x0, py - y0);
  const lineDist = vec2Dist(x0, y0, x1, y1);
  return lineDist === 0 ? undefined : crossproduct / lineDist;
}

export function vec2FromAngle(angle: number): Vec2 {
  vec2Result[0] = Math.cos(angle);
  vec2Result[1] = Math.sin(angle);
  return vec2Result;
}
